import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { TranslateModule } from '@ngx-translate/core'
import {
  ColumnTypeEnum,
  GbDashboardSharedFeatureModule,
  GbTableColumn,
  GbTableItem,
} from '@galabid/gb-dashboard/shared/feature'
import { GbDashboardSharedUiModule } from 'libs/gb-dashboard/shared/ui/src/lib/gb-dashboard-shared-ui.module'
import { TeamsFacade } from 'libs/gb-dashboard/team/data-access/src/lib/+state/teams.facade'
import { TeamsEntity } from 'libs/gb-dashboard/team/data-access/src/lib/+state/teams.models'
import { combineLatest, distinctUntilChanged, fromEvent, map, of, shareReplay, take } from 'rxjs'
import * as TeamsActions from 'libs/gb-dashboard/team/data-access/src/lib/+state/teams.actions'
import { GbDashboardTeamDataAccessModule } from 'libs/gb-dashboard/team/data-access/src/lib/gb-dashboard-team-data-access.module'

export interface GbPresenterTeamFilterDialogData {
  hiddenTeamUuids: string[]
}

export interface GbPresenterTeamFilterDialogResult {
  hiddenTeamUuids: string[]
}

@Component({
  selector: 'gb-presenter-team-filter-dialog',
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    TranslateModule,
    GbDashboardSharedFeatureModule,
    GbDashboardSharedUiModule,
    GbDashboardTeamDataAccessModule,
  ],
  templateUrl: './gb-presenter-team-filter-dialog.component.html',
  styleUrl: './gb-presenter-team-filter-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterTeamFilterDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('teamsTableContainer') teamsTableContainer?: ElementRef<HTMLDivElement>

  selectedTeams: string[] = []
  hiddenTeamUuids: string[] = []
  isLoadingMore = false

  loaded$ = this.teamsFacade.loaded$
  count$ = this.teamsFacade.count$

  teams$ = this.teamsFacade.allTeams$.pipe(
    map((teams) =>
      (teams || [])
        .slice()
        .sort((teamA, teamB) => (teamA.order ?? 0) - (teamB.order ?? 0) || teamA.name.localeCompare(teamB.name)),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  tableData$ = this.teams$.pipe(
    map((teams): GbTableItem[] =>
      teams.map((team) => ({
        uuid: team.uuid,
        name: team.name,
        profiles_count: team.profiles_count,
      })),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  displayedColumns$ = of<GbTableColumn[]>([
    {
      columnKey: 'name',
      columnLabel: 'TEAMS.NAME',
      columnType: ColumnTypeEnum.Label,
    },
    {
      columnKey: 'profiles_count',
      columnLabel: 'TEAMS.PROFILES_COUNT',
      columnType: ColumnTypeEnum.Number,
      disableSort: true,
    },
  ])

  vm$ = combineLatest([this.teams$, this.loaded$, this.count$]).pipe(
    map(([teams, loaded, count]) => ({ teams, loaded, count })),
    shareReplay({ bufferSize: 1, refCount: true }),
  )

  constructor(
    private teamsFacade: TeamsFacade,
    private dialogRef: MatDialogRef<GbPresenterTeamFilterDialogComponent, GbPresenterTeamFilterDialogResult>,
    private destroyRef: DestroyRef,
    @Inject(MAT_DIALOG_DATA) public data: GbPresenterTeamFilterDialogData,
  ) {}

  ngOnInit(): void {
    this.hiddenTeamUuids = [...(this.data.hiddenTeamUuids || [])]

    this.teamsFacade.dispatch(
      TeamsActions.loadTeams({
        ordering: 'order',
        searchTerm: '',
        shouldReset: true,
      }),
    )

    this.teams$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((teams) => {
      this.syncSelectedTeams(teams)
    })

    this.loaded$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((loaded) => {
      if (loaded) {
        this.isLoadingMore = false
      }
    })
  }

  ngAfterViewInit(): void {
    this.setupInfiniteScroll()
  }

  private setupInfiniteScroll(): void {
    const container = this.teamsTableContainer?.nativeElement
    if (!container) return

    fromEvent(container, 'scroll')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const { scrollTop, scrollHeight, clientHeight } = container
        const scrollThreshold = 100

        if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
          this.loadMoreTeams()
        }
      })
  }

  private loadMoreTeams(): void {
    if (this.isLoadingMore) return

    combineLatest([this.teams$, this.count$, this.loaded$])
      .pipe(take(1))
      .subscribe(([teams, count, loaded]) => {
        if (loaded && teams.length < count) {
          this.isLoadingMore = true
          this.teamsFacade.dispatch(
            TeamsActions.loadTeams({
              ordering: 'order',
              searchTerm: '',
              shouldReset: false,
            }),
          )
        }
      })
  }

  private syncSelectedTeams(teams: TeamsEntity[]): void {
    const hiddenSet = new Set(this.hiddenTeamUuids)
    this.selectedTeams = teams.filter((team) => !hiddenSet.has(team.uuid)).map((team) => team.uuid)
  }

  onSelectedTeamsChange(selectedTeams: string[]): void {
    this.teams$.pipe(take(1)).subscribe((teams) => {
      const hiddenSet = new Set(this.hiddenTeamUuids)

      teams.forEach((team) => {
        if (selectedTeams.includes(team.uuid)) {
          hiddenSet.delete(team.uuid)
        } else {
          hiddenSet.add(team.uuid)
        }
      })

      this.hiddenTeamUuids = [...hiddenSet]
      this.syncSelectedTeams(teams)
    })
  }

  cancel(): void {
    this.dialogRef.close()
  }

  apply(): void {
    this.dialogRef.close({ hiddenTeamUuids: this.hiddenTeamUuids })
  }
}
